import { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { buildApiUrl } from '@/api/baseUrl';

// null = 正在探测, true = 在线, false = 离线
export function useNetworkStatus(): boolean | null {
  const [isOnline, setIsOnline] = useState<boolean | null>(null);
  const wasOffline = useRef(false);

  const probe = useCallback(async () => {
    let online = false;

    if (Capacitor.isNativePlatform()) {
      // Native iOS: 用 @capacitor/network 读原生网络状态
      try {
        const { Network } = await import('@capacitor/network');
        const status = await Network.getStatus();
        online = status.connected;
      } catch {
        // plugin 不可用时降级为 navigator.onLine
        online = navigator.onLine;
      }
    } else {
      // Web: HTTP 探测
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 6000);
        await fetch(buildApiUrl('/healthz'), {
          method: 'HEAD',
          cache: 'no-store',
          redirect: 'manual',
          signal: ctrl.signal,
        });
        clearTimeout(t);
        online = true;
      } catch {
        online = false;
      }
    }

    if (online && wasOffline.current) {
      window.location.reload();
      return;
    }
    if (!online) wasOffline.current = true;
    setIsOnline(online);
  }, []);

  useEffect(() => {
    probe();

    if (Capacitor.isNativePlatform()) {
      // Native: 监听原生网络事件
      let unlistenFn: (() => void) | null = null;
      import('@capacitor/network').then(({ Network }) => {
        Network.addListener('networkStatusChange', status => {
          if (status.connected && wasOffline.current) {
            window.location.reload();
          } else if (!status.connected) {
            wasOffline.current = true;
            setIsOnline(false);
          } else {
            setIsOnline(true);
          }
        }).then(handle => {
          unlistenFn = () => handle.remove();
        });
      }).catch(() => {});
      return () => { unlistenFn?.(); };
    }

    // Web: 监听 online/offline 事件
    const handleOnline = () => probe();
    const handleOffline = () => { wasOffline.current = true; setIsOnline(false); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [probe]);

  return isOnline;
}
